import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Dialog,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Analytics as AnalyticsIcon,
  Receipt as ReceiptIcon,
  ArrowForward as ArrowForwardIcon,
  BarChart as BarChartIcon,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
  Delete as DeleteIcon,
  ExitToApp as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  Favorite as FavoriteIcon,
  Gavel as GavelIcon,
  InfoOutlined as InfoIcon,
  Notifications as NotificationsIcon,
  PauseCircle as PauseCircleIcon,
  PlayCircleOutline as PlayIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  RequestPage as RequestPageIcon,
  SearchOutlined as SearchIcon,
  Settings as SettingsIcon,
  Storefront as StorefrontIcon,
  SwapHoriz as ReservationIcon,
  Tune as TuneIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { CategoryProvider, useCategories } from '../context/CategoryContext';
import CategoryGridPlus from '../components/categories/CategoryGridPlus';
import CategoryForm from '../components/categories/CategoryForm';
import CategoryDragList from '../components/categories/CategoryDragList';
import CategoryStats from '../components/categories/CategoryStats';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StripeAccountStatusCard from '../components/financial/StripeAccountStatusCard';
import categoryService from '../services/categoryService';
import WelcomeGuideDialog from '../components/auth/WelcomeGuideDialog';

const sectionCardSx = {
  borderRadius: 4,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)'
};

const actionCardSx = (color) => ({
  height: '100%',
  borderRadius: 3,
  border: `1px solid ${color}22`,
  background: `linear-gradient(180deg, ${color}12 0%, #ffffff 58%)`,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: `${color}66`,
    boxShadow: `0 18px 34px ${color}20`
  }
});

const ActionCard = ({ title, description, icon: Icon, color, onClick, badge, ctaLabel }) => (
  <Card sx={actionCardSx(color)}>
    <CardActionArea onClick={onClick} sx={{ height: '100%', p: 0 }}>
      <CardContent sx={{ p: 2.5, height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `${color}18`,
              color
            }}
          >
            <Icon />
          </Box>
          {badge ? (
            <Chip
              label={badge}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: `${color}14`,
                color,
                border: `1px solid ${color}28`
              }}
            />
          ) : null}
        </Stack>

        <Typography sx={{ mt: 2, fontWeight: 700, color: '#0f172a' }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 0.8, color: 'text.secondary', minHeight: 44 }}>
          {description}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 2, color, fontWeight: 700 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {ctaLabel}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 18 }} />
        </Stack>
      </CardContent>
    </CardActionArea>
  </Card>
);

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const categoryWorkspaceRef = useRef(null);
  const openActionLabel = t('dashboardPage.actionCard.open', 'Open');

  const [categoryView, setCategoryView] = useState('list');
  const [openForm, setOpenForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [cacheAction, setCacheAction] = useState({ loading: false, type: null });
  const [showGuide, setShowGuide] = useState(false);

  const isAdmin = Boolean(user?.isAdmin);
  const isOwner = Boolean(user?.isOwner);
  const isTenant = Boolean(user?.isTenant);
  const hasAdminAccess = isAdmin;

  const roleLabels = [
    isTenant ? { key: 'tenant', label: t('profile.tenant', 'Locataire'), color: '#2563eb' } : null,
    isOwner ? { key: 'owner', label: t('profile.owner', 'Proprietaire'), color: '#db2777' } : null,
    isAdmin ? { key: 'admin', label: t('navigation.admin', 'Admin'), color: '#7c3aed' } : null
  ].filter(Boolean);

  const badgeLabels = {
    admin: t('dashboardPage.badges.admin', 'Admin'),
    owner: t('dashboardPage.badges.owner', 'Owner'),
    catalogue: t('dashboardPage.badges.catalogue', 'Catalogue'),
    secure: t('dashboardPage.badges.secure', 'Secure')
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleReservationsCenter = () => {
    navigate('/reservations-owner');
  };

  const handleFinancialCenter = () => {
    navigate('/financial-owner');
  };

  const openCategoryWorkspace = (view = 'list') => {
    setCategoryView(view);
    window.requestAnimationFrame(() => {
      categoryWorkspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const quickAccessCards = [
    {
      key: 'profile',
      title: t('navigation.profile', 'Profil'),
      description: t('dashboardPage.quickAccess.profile.description', 'Modifier vos informations, votre photo et vos preferences.'),
      icon: PersonIcon,
      color: '#2563eb',
      onClick: () => navigate('/profile')
    },
    {
      key: 'notifications',
      title: t('navigation.notifications', 'Notifications'),
      description: t('dashboardPage.quickAccess.notifications.description', 'Consulter vos alertes, suivis et messages systeme.'),
      icon: NotificationsIcon,
      color: '#f59e0b',
      onClick: () => navigate('/notifications')
    },
    {
      key: 'notification-preferences',
      title: t('navigation.notificationPreferences', 'Preferences de notifications'),
      description: t('dashboardPage.quickAccess.notificationPreferences.description', 'Regler les canaux, priorites et permissions de contact.'),
      icon: SettingsIcon,
      color: '#64748b',
      onClick: () => navigate('/notifications/preferences')
    },
    {
      key: 'favorites',
      title: t('common.favorites', 'Favoris'),
      description: t('dashboardPage.quickAccess.favorites.description', 'Retrouver vos elements sauvegardes et vos pistes actives.'),
      icon: FavoriteIcon,
      color: '#e11d48',
      onClick: () => navigate('/favorites')
    },
    {
      key: 'requests',
      title: t('navigation.myRequests', 'Mes demandes'),
      description: t('dashboardPage.quickAccess.myRequests.description', 'Suivre vos demandes, echanges et validations en cours.'),
      icon: RequestPageIcon,
      color: '#8b5cf6',
      onClick: () => navigate('/my-requests')
    },
    {
      key: 'analytics',
      title: t('navigation.analytics', 'Analytique'),
      description: t('dashboardPage.quickAccess.analytics.description', 'Acceder a votre espace d analyse et de pilotage.'),
      icon: AnalyticsIcon,
      color: '#0f766e',
      onClick: () => navigate('/my-dashboard')
    },
    {
      key: 'reservation-center',
      title: t('navigation.reservationCenter', 'Centre de Commandes'),
      description: t('dashboardPage.quickAccess.reservationCenter.description', 'Piloter les reservations et commandes depuis le flux central.'),
      icon: ReservationIcon,
      color: '#dc2626',
      onClick: handleReservationsCenter
    },
    {
      key: 'finance-center',
      title: t('navigation.financesCenter', 'Centre des Finances'),
      description: t('dashboardPage.quickAccess.financialCenter.description', 'Suivre vos flux financiers, versements et mouvements.'),
      icon: WalletIcon,
      color: '#059669',
      onClick: handleFinancialCenter
    }
  ];

  const managementCards = [
    {
      key: 'my-listings',
      title: t('navigation.myListings', 'Mes annonces'),
      description: t('dashboardPage.management.myListings.description', 'Gerer votre catalogue, vos fiches et vos disponibilites.'),
      icon: StorefrontIcon,
      color: '#15803d',
      onClick: () => navigate('/my-listings'),
      visible: isOwner
    },
    {
      key: 'create-listing',
      title: t('navigation.createListing', 'Creer une annonce'),
      description: t('dashboardPage.management.createListing.description', 'Ajouter rapidement une nouvelle annonce a votre parc.'),
      icon: AddIcon,
      color: '#16a34a',
      onClick: () => navigate('/create-listing'),
      visible: isOwner
    },
    {
      key: 'activity-suspension',
      title: t('dashboardPage.management.activitySuspension.title', 'Suspendre mon activité'),
      description: t('dashboardPage.management.activitySuspension.description', 'Masquer temporairement tous vos listings et produits du public, puis les réactiver quand vous le souhaitez.'),
      icon: PauseCircleIcon,
      color: '#ea580c',
      onClick: () => navigate('/activity-suspension'),
      visible: isOwner
    },
    {
      key: 'invoices',
      title: t('navigation.myInvoices', 'Mes Factures'),
      description: t('dashboardPage.management.invoices.description', 'Consulter et télécharger vos factures de commission propriétaire.'),
      icon: ReceiptIcon,
      color: '#7b1fa2',
      onClick: () => navigate('/owner/invoices'),
      visible: isOwner
    },
    {
      key: 'payouts',
      title: t('financial:payouts.title', 'Mes Virements'),
      description: t('dashboardPage.management.payouts.description', 'Consulter tous vos virements Stripe, tous statuts confondus.'),
      icon: WalletIcon,
      color: '#6200ee',
      onClick: () => navigate('/owner/payouts'),
      visible: isOwner
    },
    {
      key: 'category-workspace',
      title: t('categoryManagement.title', 'Gestion des categories'),
      description: t('dashboardPage.management.categoryWorkspace.description', 'Acceder a la taxonomie, la moderer ou proposer une nouvelle categorie.'),
      icon: CategoryIcon,
      color: '#d97706',
      onClick: () => openCategoryWorkspace('list'),
      visible: true,
      badge: isAdmin ? badgeLabels.admin : isOwner ? badgeLabels.owner : badgeLabels.catalogue
    },
    {
      key: 'category-stats',
      title: t('dashboardPage.management.categoryStats.title', 'Statistiques categories'),
      description: t('dashboardPage.management.categoryStats.description', 'Visualiser les performances et la structure de la taxonomie.'),
      icon: BarChartIcon,
      color: '#7c3aed',
      onClick: () => openCategoryWorkspace('stats'),
      visible: isAdmin
    },
    {
      key: 'category-order',
      title: t('dashboardPage.management.categoryOrder.title', 'Reorganisation categories'),
      description: t('dashboardPage.management.categoryOrder.description', 'Reordonner les categories et ajuster leur mise en avant.'),
      icon: TuneIcon,
      color: '#0f766e',
      onClick: () => openCategoryWorkspace('reorder'),
      visible: isAdmin
    },
    {
      key: 'admin',
      title: t('navigation.admin', 'Administration'),
      description: t('dashboardPage.management.administration.description', 'Basculer vers les outils d administration de la plateforme.'),
      icon: AdminPanelSettingsIcon,
      color: '#be185d',
      onClick: () => navigate('/admin'),
      visible: hasAdminAccess,
      badge: badgeLabels.secure
    }
  ].filter((item) => item.visible !== false);

  const supportCards = [
    {
      key: 'search',
      title: t('navigation.search', 'Recherche'),
      description: t('dashboardPage.support.search.description', 'Explorer les offres et relancer un parcours de recherche.'),
      icon: SearchIcon,
      color: '#2563eb',
      onClick: () => navigate('/search')
    },
    {
      key: 'categories',
      title: t('navigation.categories', 'Categories'),
      description: t('dashboardPage.support.categories.description', 'Naviguer dans le catalogue complet des categories publiques.'),
      icon: CategoryIcon,
      color: '#d97706',
      onClick: () => navigate('/categories')
    },
    {
      key: 'disputes',
      title: t('disputes:all.title', 'Mes litiges'),
      description: t('dashboardPage.support.disputes.description', 'Suivre les dossiers en cours et les arbitrages ouverts.'),
      icon: GavelIcon,
      color: '#1d4ed8',
      onClick: () => navigate('/disputes')
    },
    {
      key: 'penalties',
      title: t('penalties:all.title', 'Mes penalites'),
      description: t('dashboardPage.support.penalties.description', 'Controler les penalites, motifs et statuts associes.'),
      icon: GavelIcon,
      color: '#b91c1c',
      onClick: () => navigate('/penalties')
    },
    {
      key: 'how-it-works',
      title: t('navigation.howItWorks', 'Comment ca marche'),
      description: t('dashboardPage.support.howItWorks.description', 'Retrouver les regles de fonctionnement et les parcours clefs.'),
      icon: InfoIcon,
      color: '#0891b2',
      onClick: () => navigate('/how-it-works')
    },
    {
      key: 'about',
      title: t('navigation.about', 'A propos'),
      description: t('dashboardPage.support.about.description', 'Acceder aux informations de reference sur la plateforme.'),
      icon: DashboardIcon,
      color: '#475569',
      onClick: () => navigate('/about')
    }
  ];

  const CategoriesWorkspace = () => {
    const {
      categories,
      createCategory,
      updateCategory,
      deleteCategory,
      preloadCache,
      clearCache
    } = useCategories();

    useEffect(() => {
      window.dispatchEvent(new CustomEvent('refreshCategories'));
    }, []);

    const handleOpenForm = (category = null) => {
      setSelectedCategory(category);
      setOpenForm(true);
    };

    const handleCloseForm = () => {
      setOpenForm(false);
      setSelectedCategory(null);
    };

    const handleSubmitForm = async (data, isFormData = false) => {
      try {
        if (selectedCategory) {
          if (isFormData) {
            if (!(data instanceof FormData)) {
              throw new Error(t('dashboardPage.categoryWorkspace.notifications.invalidImageUploadData', 'Invalid data format for image upload'));
            }

            let hasImage = false;
            for (const pair of data.entries()) {
              if (pair[0] === 'categoryImage') {
                hasImage = true;
              }
            }

            if (!hasImage) {
              throw new Error(t('dashboardPage.categoryWorkspace.notifications.noImageFound', 'No image found in the submitted data'));
            }

            await categoryService.uploadCategoryImage(selectedCategory.id, data);
            await updateCategory(selectedCategory.id, {});
          } else {
            await updateCategory(selectedCategory.id, data);
          }

          setNotification({
            open: true,
            message: t('dashboardPage.categoryWorkspace.notifications.categoryUpdated', 'Category updated successfully'),
            severity: 'success'
          });
        } else {
          if (isOwner && !isAdmin) {
            if (data instanceof FormData) {
              data.append('pendingApproval', 'true');
              data.append('isActive', 'false');
            } else {
              data.pendingApproval = true;
              data.isActive = false;
            }
          }

          if (isFormData && data instanceof FormData) {
            let hasImage = false;
            for (const pair of data.entries()) {
              if (pair[0] === 'categoryImage') {
                hasImage = true;
              }
            }

            if (!hasImage) {
              throw new Error(t('dashboardPage.categoryWorkspace.notifications.noImageFound', 'No image found in the submitted data'));
            }

            await categoryService.createCategoryWithImage(data);
            // Recharger les catégories sans faire de nouvel appel de création
            window.dispatchEvent(new CustomEvent('refreshCategories'));
          } else {
            await createCategory(data);
          }

          // Créer le bouton d'action si c'est une demande d'approbation
          const actionButton = isOwner && !isAdmin
            ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  navigate('/my-requests');
                  handleCloseNotification();
                }}
                sx={{
                  border: '2px solid',
                  borderColor: '#065f46'
                }}
              >
                {t('dashboardPage.categoryWorkspace.notifications.viewMyRequests')}
              </Button>
            )
            : undefined;

          setNotification({
            open: true,
            message: isOwner && !isAdmin
              ? t('dashboardPage.categoryWorkspace.notifications.categoryCreatedPendingApproval', 'Category created and submitted for approval')
              : t('dashboardPage.categoryWorkspace.notifications.categoryCreated', 'Category created successfully'),
            severity: 'success',
            action: actionButton
          });
        }

        handleCloseForm();
        setCategoryView('list');

        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent('refreshCategories'));
        }, 300);
      } catch (error) {
        // Re-throw: CategoryForm gère l'affichage inline des erreurs
        throw error;
      }
    };

    const handleOpenDeleteConfirm = (category) => {
      setSelectedCategory(category);
      setConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
      try {
        await deleteCategory(selectedCategory.id);
        setNotification({
          open: true,
          message: t('dashboardPage.categoryWorkspace.notifications.categoryDeleted', 'Category deleted successfully'),
          severity: 'success'
        });
      } catch (error) {
        setNotification({
          open: true,
          message: error.response?.data?.message || t('dashboardPage.categoryWorkspace.notifications.deleteError', 'An error occurred while deleting the category'),
          severity: 'error'
        });
      } finally {
        setConfirmDelete(false);
        setSelectedCategory(null);
      }
    };

    const handleCacheAction = async (type) => {
      try {
        setCacheAction({ loading: true, type });

        if (type === 'preload') {
          await preloadCache();
          setNotification({
            open: true,
            message: t('dashboardPage.categoryWorkspace.notifications.cachePreloaded', 'Cache preloaded successfully'),
            severity: 'success'
          });
        }

        if (type === 'clear') {
          await clearCache();
          setNotification({
            open: true,
            message: t('dashboardPage.categoryWorkspace.notifications.cacheCleared', 'Cache cleared successfully'),
            severity: 'success'
          });
        }
      } catch (error) {
        setNotification({
          open: true,
          message: t('dashboardPage.categoryWorkspace.notifications.cacheActionError', 'Error during cache action: {{message}}', { message: error.message }),
          severity: 'error'
        });
      } finally {
        setCacheAction({ loading: false, type: null });
      }
    };

    const selectorCards = [
      {
        key: 'list',
        title: t('categoryManagement.tabs.list', 'Liste'),
        description: t('dashboardPage.categoryWorkspace.selector.list', 'Browse, edit and delete categories.'),
        icon: ViewListIcon,
        color: '#2563eb',
        visible: true
      },
      {
        key: 'reorder',
        title: t('categoryManagement.tabs.reorganize', 'Reorganiser'),
        description: t('dashboardPage.categoryWorkspace.selector.reorder', 'Change hierarchy and display order.'),
        icon: TuneIcon,
        color: '#0f766e',
        visible: isAdmin
      },
      {
        key: 'stats',
        title: t('categoryManagement.tabs.statistics', 'Statistiques'),
        description: t('dashboardPage.categoryWorkspace.selector.stats', 'Observe volume and taxonomy usage.'),
        icon: BarChartIcon,
        color: '#7c3aed',
        visible: isAdmin
      }
    ].filter((item) => item.visible);

    return (
      <Box ref={categoryWorkspaceRef} sx={{ mt: 5 }}>
        <Accordion
          defaultExpanded
          disableGutters
          sx={{
            ...sectionCardSx,
            overflow: 'hidden',
            '&:before': {
              display: 'none'
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: { xs: 2, md: 3 }, py: 1.25 }}
          >            
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {t('dashboardPage.categoryWorkspace.accordion.title', 'Category workspace')}
              </Typography>              
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
              {isAdmin ? (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => handleCacheAction('preload')}
                    disabled={cacheAction.loading}
                  >
                    {t('categoryManagement.preloadCache', 'Precharger le cache')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleCacheAction('clear')}
                    disabled={cacheAction.loading}
                  >
                    {t('categoryManagement.clearCache', 'Vider le cache')}
                  </Button>
                </>
              ) : null}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenForm()}
                sx={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  boxShadow: 'none'
                }}
              >
                {isOwner && !isAdmin
                  ? t('categoryManagement.proposeCategory', 'Proposer une categorie')
                  : t('categoryManagement.newCategory', 'Nouvelle categorie')}
              </Button>
            </Stack>
          </Stack>

          {isOwner && !isAdmin ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('categoryManagement.ownerInfoMessage')}
            </Alert>
          ) : null}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {selectorCards.map((item) => {
              const selected = categoryView === item.key;

              return (
                <Grid item xs={12} md={4} key={item.key}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      border: selected ? `2px solid ${item.color}` : '1px solid rgba(15, 23, 42, 0.08)',
                      background: selected
                        ? `linear-gradient(180deg, ${item.color}14 0%, #ffffff 70%)`
                        : '#ffffff',
                      boxShadow: selected ? `0 14px 28px ${item.color}18` : 'none'
                    }}
                  >
                    <CardActionArea onClick={() => setCategoryView(item.key)}>
                      <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: 2.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: `${item.color}16`,
                              color: item.color
                            }}
                          >
                            <item.icon />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                              {item.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ pt: 1 }}>
            {categoryView === 'list' ? (
              <CategoryGridPlus
                categories={categories}
                showAll
                columnsPerRow={3}
              />
            ) : null}

            {categoryView === 'reorder' && isAdmin ? <CategoryDragList /> : null}
            {categoryView === 'stats' && isAdmin ? <CategoryStats /> : null}
          </Box>

          <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="md">
            <CategoryForm
              category={selectedCategory}
              onSubmit={handleSubmitForm}
              onCancel={handleCloseForm}
              isOwner={isOwner && !isAdmin}
            />
          </Dialog>

          <ConfirmDialog
            open={confirmDelete}
            title={t('categoryManagement.deleteConfirmation.title')}
            content={t('categoryManagement.deleteConfirmation.content', { name: selectedCategory?.name })}
            onConfirm={handleConfirmDelete}
            onCancel={() => setConfirmDelete(false)}
            severity="error"
          />
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const handleCloseNotification = () => {
    setNotification((current) => ({ ...current, open: false }));
  };

  return (
    <Box
      sx={{
        py: { xs: 2, md: 4 },
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fafc 0%, #eef4ff 100%)'
      }}
    >
      <Container maxWidth="xl">
        <Paper
          className="payment-history-header"
          sx={{
            p: { xs: 2.2, md: 3.2 },
            mb: 3,
            color: 'white',
            borderRadius: 4,
            overflow: 'hidden'
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={7}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <DashboardIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>

                <Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                    {t('navigation.dashboard')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.92)' }}>
                    {t('common.greeting', { name: user?.name })}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: 'white'
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar src={user?.profilePicture} sx={{ width: 58, height: 58 }}>
                      {user?.name?.charAt(0) || 'U'}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{user?.name}</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.88)' }}>
                        {user?.email}
                      </Typography>
                      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        {roleLabels.map((role) => (
                          <Chip
                            key={role.key}
                            size="small"
                            label={role.label}
                            sx={{
                              color: 'white',
                              bgcolor: `${role.color}cc`,
                              fontWeight: 700
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Stack>

                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.34)',
                      bgcolor: 'rgba(255,255,255,0.08)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.5)',
                        bgcolor: 'rgba(255,255,255,0.14)'
                      }
                    }}
                  >
                    {t('common.logout')}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Guide de bienvenue */}
        <Paper
          sx={{
            mb: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white',
            boxShadow: '0 4px 15px rgba(98, 0, 238, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
              {t('welcome.rewatch', 'Revoir le guide')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.92)', mt: 0.5 }}>
              {t('welcome.subtitle', 'Besoin d\'aide ? Revoyez la video de presentation.')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<PlayIcon />}
            onClick={() => setShowGuide(true)}
            sx={{
              borderColor: 'rgba(255,255,255,0.5)',
              color: 'white',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.15)',
              },
            }}
          >
            {t('welcome.rewatch', 'Revoir le guide')}
          </Button>
        </Paper>

        <WelcomeGuideDialog open={showGuide} onClose={() => setShowGuide(false)} />

        <Grid container spacing={3}>
          <Grid item xs={12} xl={8}>
            <Paper sx={{ ...sectionCardSx, p: { xs: 2, md: 3 } }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {t('dashboardPage.sections.accessPriorities.title', 'Acces prioritaires')}
              </Typography>
              <Typography sx={{ mt: 0.75, mb: 3, color: 'text.secondary' }}>
                {t('dashboardPage.sections.accessPriorities.description', 'Les liens critiques du menu principal sont centralises ici pour accelerer la navigation quotidienne.')}
              </Typography>

              <Grid container spacing={2}>
                {quickAccessCards.map((card) => (
                  <Grid item xs={12} sm={6} xl={3} key={card.key}>
                    <ActionCard {...card} ctaLabel={openActionLabel} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} xl={4}>
            <Paper sx={{ ...sectionCardSx, p: { xs: 2, md: 3 }, height: '100%' }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {t('dashboardPage.sections.accountView.title', 'Vue compte')}
              </Typography>
              <Typography sx={{ mt: 0.75, color: 'text.secondary' }}>
                {t('dashboardPage.sections.accountView.description', 'Votre espace est organise autour des acces reels disponibles pour le compte connecte.')}
              </Typography>

              <Stack spacing={1.25} sx={{ mt: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                  <Typography variant="overline" sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
                    {t('dashboardPage.sections.accountView.activeRoles', 'Roles actifs')}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    {roleLabels.map((role) => (
                      <Chip
                        key={role.key}
                        label={role.label}
                        sx={{ bgcolor: `${role.color}14`, color: role.color, fontWeight: 700 }}
                      />
                    ))}
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                  <Typography variant="overline" sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
                    {t('dashboardPage.sections.accountView.availableNavigation', 'Navigation disponible')}
                  </Typography>
                  <Typography sx={{ mt: 1, color: '#0f172a', fontWeight: 700 }}>
                    {t('dashboardPage.sections.accountView.accessPoints', '{{count}} points d acces directs', { count: quickAccessCards.length + managementCards.length + supportCards.length })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {t('dashboardPage.sections.accountView.noFakeIndicators', 'Aucun indicateur factice: cette page sert d interface de pilotage et de redirection vers les vrais espaces metier.')}
                  </Typography>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                  <Typography variant="overline" sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
                    {t('dashboardPage.sections.accountView.priorityAxis', 'Axe prioritaire')}
                  </Typography>
                  <Typography sx={{ mt: 1, color: '#0f172a', fontWeight: 700 }}>
                    {t('dashboardPage.sections.accountView.priorityDescription', 'Catalogue, transactions et support reunis sur un seul ecran.')}
                  </Typography>
                </Paper>
              </Stack>
              <Box sx={{ mt: 2 }}>
                <StripeAccountStatusCard user={user} variant="card" />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ ...sectionCardSx, p: { xs: 2, md: 3 } }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {t('dashboardPage.sections.businessManagement.title', 'Pilotage metier')}
              </Typography>
              <Typography sx={{ mt: 0.75, mb: 3, color: 'text.secondary' }}>
                {t('dashboardPage.sections.businessManagement.description', 'Les espaces annonces, categories et administration sont accessibles sans onglets ni ecran intermediaire.')}
              </Typography>

              <Grid container spacing={2}>
                {managementCards.map((card) => (
                  <Grid item xs={12} sm={6} lg={3} key={card.key}>
                    <ActionCard {...card} ctaLabel={openActionLabel} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ ...sectionCardSx, p: { xs: 2, md: 3 } }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {t('dashboardPage.sections.supportAndExploration.title', 'Support et exploration')}
              </Typography>
              <Typography sx={{ mt: 0.75, mb: 3, color: 'text.secondary' }}>
                {t('dashboardPage.sections.supportAndExploration.description', 'Recherche, categories publiques, contentieux et pages d information restent accessibles depuis le tableau de bord.')}
              </Typography>

              <Grid container spacing={2}>
                {supportCards.map((card) => (
                  <Grid item xs={12} sm={6} lg={4} xl={2} key={card.key}>
                    <ActionCard {...card} ctaLabel={openActionLabel} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <CategoryProvider>
              <CategoriesWorkspace />
            </CategoryProvider>
          </Grid>
        </Grid>

        <Snackbar
          open={notification.open}
          autoHideDuration={10000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity} action={notification.action}>
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default DashboardPage;